import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Briefcase, Plus, Pencil, UserCheck, Copy, Eye, Download } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline cell used in the applications table to link a loan to an agent
export const AgentCell = ({ app, getAuthHeader, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(app.sales_agent_id || '');
  const [saving, setSaving] = useState(false);

  const openDialog = async (e) => {
    e.stopPropagation();
    setSelected(app.sales_agent_id || '');
    setOpen(true);
    try {
      const res = await axios.get(`${API_URL}/admin/agents`, getAuthHeader());
      setAgents(res.data);
    } catch { toast.error('Failed to load agents'); }
  };

  const save = async (agentId) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/customer-applications/${app.id}/agent`,
        { agent_id: agentId }, getAuthHeader());
      toast.success(agentId ? 'Agent linked to loan' : 'Agent unlinked');
      setOpen(false);
      onUpdated && onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update agent'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button onClick={openDialog} data-testid={`agent-cell-${app.id}`}
        className={`text-xs px-2 py-1 rounded-md border transition-colors ${app.sales_agent_name
          ? 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100'
          : 'bg-slate-50 border-dashed border-slate-300 text-slate-400 hover:text-slate-600 hover:border-slate-400'}`}>
        {app.sales_agent_name ? (
          <span>
            {app.sales_agent_name}
            {app.agent_commission_rate ? <span className="block text-[10px] text-indigo-400">{app.agent_commission_rate}%</span> : null}
          </span>
        ) : '+ Agent'}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" onClick={e => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserCheck className="w-4 h-4" /> Link Agent for Commission</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Loan {app.application_number} — {app.full_name}. Commission = agent rate × approved amount.</p>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="agent-select-trigger"><SelectValue placeholder="Select agent..." /></SelectTrigger>
              <SelectContent>
                {agents.map(ag => (
                  <SelectItem key={ag.id} value={ag.id}>
                    {ag.full_name}{ag.agency_name ? ` — ${ag.agency_name}` : ''} ({ag.commission_rate}%)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button className="flex-1" disabled={!selected || saving} onClick={() => save(selected)} data-testid="agent-assign-save-btn">
                {saving ? 'Saving...' : 'Link Agent'}
              </Button>
              {app.sales_agent_id && (
                <Button variant="outline" disabled={saving} onClick={() => save('')} data-testid="agent-unassign-btn">Unlink</Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const emptyForm = { full_name: '', email: '', phone: '', agency_name: '', commission_rate: '6', password: '' };

export default function AgentsPanel({ getAuthHeader }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editDialog, setEditDialog] = useState({ open: false, agent: null });
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, agent: null, report: null });
  const [payoutDialog, setPayoutDialog] = useState({ open: false, month: null, amount: '', notes: '' });

  const openDetail = async (agent) => {
    setDetailDialog({ open: true, agent, report: null });
    try {
      const res = await axios.get(`${API_URL}/admin/agents/${agent.id}/summary`, getAuthHeader());
      setDetailDialog({ open: true, agent, report: res.data });
    } catch { toast.error('Failed to load agent summary'); }
  };

  const markPaid = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/agents/${detailDialog.agent.id}/payouts`,
        { month: payoutDialog.month, amount: parseFloat(payoutDialog.amount), notes: payoutDialog.notes },
        getAuthHeader());
      toast.success(`Payout for ${payoutDialog.month} marked as paid`);
      setPayoutDialog({ open: false, month: null, amount: '', notes: '' });
      openDetail(detailDialog.agent);
      fetchAgents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark payout'); }
    finally { setSaving(false); }
  };

  const unmarkPaid = async (month) => {
    try {
      await axios.delete(`${API_URL}/admin/agents/${detailDialog.agent.id}/payouts/${month}`, getAuthHeader());
      toast.success(`Payout for ${month} unmarked`);
      openDetail(detailDialog.agent);
      fetchAgents();
    } catch { toast.error('Failed to unmark payout'); }
  };

  const downloadStatement = async (agentId, month) => {
    try {
      const res = await axios.get(`${API_URL}/admin/agents/${agentId}/statement/${month}`, {
        ...getAuthHeader(), responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `2k2day_agent_statement_${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download statement'); }
  };

  const copyReferral = async (code) => {
    const link = `${window.location.origin}/apply?ref=${code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Referral link copied');
    } catch {
      window.prompt('Copy the referral link:', link);
    }
  };

  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/agents`, getAuthHeader());
      setAgents(res.data);
    } catch { toast.error('Failed to load agents'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const createAgent = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/agents`, {
        ...form, commission_rate: parseFloat(form.commission_rate)
      }, getAuthHeader());
      toast.success('Agent created — they can log in at /client/login');
      setCreateOpen(false);
      setForm(emptyForm);
      fetchAgents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create agent'); }
    finally { setSaving(false); }
  };

  const updateAgent = async () => {
    setSaving(true);
    try {
      const payload = { ...editForm };
      if (payload.commission_rate !== undefined) payload.commission_rate = parseFloat(payload.commission_rate);
      if (!payload.new_password) delete payload.new_password;
      await axios.put(`${API_URL}/admin/agents/${editDialog.agent.id}`, payload, getAuthHeader());
      toast.success('Agent updated');
      setEditDialog({ open: false, agent: null });
      fetchAgents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update agent'); }
    finally { setSaving(false); }
  };

  const totalPending = agents.reduce((s, a) => s + (a.commission_pending || 0), 0);
  const totalEarned = agents.reduce((s, a) => s + (a.commission_earned || 0), 0);

  return (
    <div className="space-y-4" data-testid="agents-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Agents</p>
          <p className="text-2xl font-bold" data-testid="agents-total-count">{agents.length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Linked Loans</p>
          <p className="text-2xl font-bold">{agents.reduce((s, a) => s + (a.loans_count || 0), 0)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Commission Pending</p>
          <p className="text-2xl font-bold text-amber-600" data-testid="agents-pending-total">R{totalPending.toLocaleString()}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Commission Earned</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="agents-earned-total">R{totalEarned.toLocaleString()}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5" /> Agents & Agencies</CardTitle>
            <CardDescription>Independent agents earn commission (% of approved capital) on loans linked to them. Pending = approved, Earned = repaid.</CardDescription>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="add-agent-btn"><Plus className="w-4 h-4 mr-1" /> Add Agent</Button>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-center text-slate-500 py-8">Loading...</p> :
          agents.length === 0 ? <p className="text-center text-slate-500 py-8">No agents yet. Add your first agent/agency.</p> : (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="agents-table">
                <thead>
                  <tr>
                    <th>Agent</th><th>Agency</th><th>Email</th><th>Rate</th><th>Referral</th><th>Loans</th><th>Clients</th>
                    <th>Capital</th><th>Pending</th><th>Earned</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map(ag => (
                    <tr key={ag.id}>
                      <td className="font-medium">{ag.full_name}</td>
                      <td className="text-sm text-slate-600">{ag.agency_name || '—'}</td>
                      <td className="text-xs text-slate-500">{ag.email}</td>
                      <td><Badge variant="outline" className="bg-indigo-50 text-indigo-700">{ag.commission_rate}%</Badge></td>
                      <td>
                        {ag.referral_code ? (
                          <button className="inline-flex items-center gap-1 text-xs font-mono text-indigo-600 hover:underline" title="Copy referral apply link"
                            onClick={() => copyReferral(ag.referral_code)} data-testid={`copy-referral-${ag.id}`}>
                            {ag.referral_code} <Copy className="w-3 h-3" />
                          </button>
                        ) : '—'}
                      </td>
                      <td className="tabular-nums">{ag.loans_count}</td>
                      <td className="tabular-nums">{ag.clients_count}</td>
                      <td className="tabular-nums">R{(ag.capital_total || 0).toLocaleString()}</td>
                      <td className="tabular-nums text-amber-600 font-medium">R{(ag.commission_pending || 0).toLocaleString()}</td>
                      <td className="tabular-nums text-emerald-600 font-medium">R{(ag.commission_earned || 0).toLocaleString()}</td>
                      <td>
                        <Badge variant="outline" className={ag.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                          {ag.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex items-center">
                          <Button variant="ghost" size="sm" title="Commission & payouts" data-testid={`view-agent-${ag.id}`}
                            onClick={() => openDetail(ag)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" data-testid={`edit-agent-${ag.id}`}
                            onClick={() => { setEditForm({ full_name: ag.full_name, phone: ag.phone || '', agency_name: ag.agency_name || '', commission_rate: String(ag.commission_rate || ''), is_active: ag.is_active !== false, new_password: '' }); setEditDialog({ open: true, agent: ag }); }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Agent / Agency</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full Name</Label><Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="agent-form-name" /></div>
            <div><Label>Agency Name</Label><Input value={form.agency_name} onChange={e => setForm({ ...form, agency_name: e.target.value })} placeholder="e.g. ABC Financial Services" data-testid="agent-form-agency" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email (login)</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="agent-form-email" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="agent-form-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Commission Rate (% of capital)</Label><Input type="number" step="0.1" value={form.commission_rate} onChange={e => setForm({ ...form, commission_rate: e.target.value })} data-testid="agent-form-rate" /></div>
              <div><Label>Password</Label><Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="agent-form-password" /></div>
            </div>
            <Button className="w-full" disabled={saving || !form.full_name || !form.email || !form.password || !form.commission_rate} onClick={createAgent} data-testid="agent-form-submit">
              {saving ? 'Creating...' : 'Create Agent'}
            </Button>
            <p className="text-xs text-slate-400">The agent logs in with this email &amp; password on the client login page and is redirected to their agent portal.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editDialog.open} onOpenChange={o => setEditDialog({ open: o, agent: o ? editDialog.agent : null })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Agent — {editDialog.agent?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full Name</Label><Input value={editForm.full_name || ''} onChange={e => setEditForm({ ...editForm, full_name: e.target.value })} data-testid="agent-edit-name" /></div>
            <div><Label>Agency Name</Label><Input value={editForm.agency_name || ''} onChange={e => setEditForm({ ...editForm, agency_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={editForm.phone || ''} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} /></div>
              <div><Label>Commission Rate (%)</Label><Input type="number" step="0.1" value={editForm.commission_rate || ''} onChange={e => setEditForm({ ...editForm, commission_rate: e.target.value })} data-testid="agent-edit-rate" /></div>
            </div>
            <div><Label>New Password (optional)</Label><Input type="text" value={editForm.new_password || ''} onChange={e => setEditForm({ ...editForm, new_password: e.target.value })} placeholder="Leave blank to keep current" /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="agent-active" className="rounded" checked={editForm.is_active !== false} onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })} />
              <Label htmlFor="agent-active">Active</Label>
            </div>
            <Button className="w-full" disabled={saving} onClick={updateAgent} data-testid="agent-edit-save">{saving ? 'Saving...' : 'Save Changes'}</Button>
            <p className="text-xs text-slate-400">Note: rate changes apply to newly linked loans. Loans already linked keep the rate at time of linking.</p>
          </div>
        </DialogContent>
      </Dialog>
      {/* Agent detail / payouts dialog */}
      <Dialog open={detailDialog.open} onOpenChange={o => setDetailDialog(o ? detailDialog : { open: false, agent: null, report: null })}>
        <DialogContent className="max-w-3xl" data-testid="agent-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" /> {detailDialog.agent?.full_name} — Commission &amp; Payouts
            </DialogTitle>
          </DialogHeader>
          {!detailDialog.report ? <p className="text-center text-slate-500 py-8">Loading...</p> : (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3 text-center">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[11px] text-slate-500 uppercase">Pending</p>
                  <p className="font-bold text-amber-600">R{detailDialog.report.stats.commission_pending.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[11px] text-slate-500 uppercase">Earned</p>
                  <p className="font-bold text-emerald-600">R{detailDialog.report.stats.commission_earned.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[11px] text-slate-500 uppercase">Total</p>
                  <p className="font-bold">R{detailDialog.report.stats.commission_total.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[11px] text-slate-500 uppercase">Paid Out</p>
                  <p className="font-bold text-indigo-600" data-testid="agent-paid-out-total">R{(detailDialog.report.stats.paid_out_total || 0).toLocaleString()}</p>
                </div>
              </div>
              {detailDialog.report.monthly.length === 0 ? (
                <p className="text-center text-slate-500 py-6">No loans linked to this agent yet.</p>
              ) : (
                <div className="overflow-x-auto max-h-[45vh] overflow-y-auto">
                  <table className="data-table" data-testid="agent-payout-table">
                    <thead>
                      <tr><th>Month</th><th>Loans</th><th>Capital</th><th>Commission</th><th>Payout Status</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {detailDialog.report.monthly.map(m => (
                        <tr key={m.month}>
                          <td className="font-medium">{m.month}</td>
                          <td className="tabular-nums">{m.loans}</td>
                          <td className="tabular-nums">R{m.capital.toLocaleString()}</td>
                          <td className="tabular-nums font-semibold">R{m.commission_total.toLocaleString()}
                            <span className="block text-[10px] font-normal text-slate-400">R{m.commission_pending.toLocaleString()} pending · R{m.commission_earned.toLocaleString()} earned</span>
                          </td>
                          <td>
                            {m.is_paid ? (
                              <Badge variant="outline" className="bg-emerald-100 text-emerald-700" data-testid={`admin-payout-badge-${m.month}`}>
                                Paid R{(m.payout_amount || 0).toLocaleString()} · {(m.paid_at || '').slice(0, 10)}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-slate-100 text-slate-500">Unpaid</Badge>
                            )}
                          </td>
                          <td>
                            <div className="flex items-center gap-1">
                              {m.is_paid ? (
                                <Button variant="ghost" size="sm" className="text-xs text-red-500" data-testid={`unmark-paid-${m.month}`}
                                  onClick={() => unmarkPaid(m.month)}>Unmark</Button>
                              ) : (
                                <Button variant="outline" size="sm" className="text-xs" data-testid={`mark-paid-${m.month}`}
                                  onClick={() => setPayoutDialog({ open: true, month: m.month, amount: String(m.commission_total), notes: '' })}>
                                  Mark Paid
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" title="Statement PDF" data-testid={`admin-statement-${m.month}`}
                                onClick={() => downloadStatement(detailDialog.agent.id, m.month)}>
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark paid dialog */}
      <Dialog open={payoutDialog.open} onOpenChange={o => setPayoutDialog(o ? payoutDialog : { open: false, month: null, amount: '', notes: '' })}>
        <DialogContent className="max-w-sm" data-testid="payout-dialog">
          <DialogHeader><DialogTitle>Mark {payoutDialog.month} as Paid Out</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Amount Paid (R)</Label><Input type="number" step="0.01" value={payoutDialog.amount} onChange={e => setPayoutDialog({ ...payoutDialog, amount: e.target.value })} data-testid="payout-amount-input" /></div>
            <div><Label>Notes (optional)</Label><Textarea rows={2} value={payoutDialog.notes} onChange={e => setPayoutDialog({ ...payoutDialog, notes: e.target.value })} placeholder="e.g. EFT ref 12345" data-testid="payout-notes-input" /></div>
            <Button className="w-full" disabled={saving || payoutDialog.amount === ''} onClick={markPaid} data-testid="payout-confirm-btn">
              {saving ? 'Saving...' : 'Confirm Payout'}
            </Button>
            <p className="text-xs text-slate-400">The agent will see this month marked as paid on their portal and statement.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
